import { useCallback, useEffect, useState } from "react";
import { Inbox, Loader2, Rocket } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STATUSES = [["requested", "Requested"], ["in_progress", "Being built"],
                  ["registered", "Registered"], ["live", "Live"], ["declined", "Declined"]];
const STORE = { android: "Google Play", ios: "App Store" };

/** Operators asking for their own store app, and one press to register it. */
export const AppRequestQueue = ({ onRegister, onNotice }) => {
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setData((await api.get("/admin/app-requests")).data);
    } catch (e) {
      setError(errMsg(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (request, status) => {
    setBusy(true); setError("");
    try {
      await api.post(`/admin/app-requests/${request.id}`,
        { status, admin_note: notes[request.id] ?? request.admin_note ?? "" });
      onNotice?.(`${request.company_name} marked ${status.replace("_", " ")}.`);
      await load();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <Loader2 data-testid="app-requests-loading" className="h-5 w-5 animate-spin text-gold" />;
  if (!data.requests.length) return null;

  return (
    <div data-testid="app-request-queue" className="rounded-xl border border-gold/30 bg-ink-800 p-5">
      <p className="flex items-center gap-2 font-display text-lg text-slate-100">
        <Inbox className="h-4 w-4 text-gold" /> Operator app requests
        {Boolean(data.open) && (
          <span data-testid="app-requests-open" className="rounded-full bg-gold px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-ink-900">
            {data.open} open
          </span>
        )}
      </p>
      {error && <p data-testid="app-requests-error" className="mt-2 text-sm text-red-400">{error}</p>}
      <div className="mt-4 space-y-3">
        {data.requests.map((r) => (
          <div key={r.id} data-testid={`app-request-${r.id}`}
            className="rounded-lg border border-ink-600 bg-ink-900/50 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-display text-slate-100">
                  {r.company_name}
                  <span className="ml-2 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                    {r.plan} · {r.stores.map((s) => STORE[s]).join(" + ")}
                  </span>
                </p>
                <p className="mt-1 font-mono text-[11px] text-slate-500">
                  {r.app_name} · {r.suggested_app_id || "no handle yet"}
                  {!r.icon_ready && <span className="ml-2 text-amber-400">no app icon uploaded</span>}
                </p>
                {r.note && <p className="mt-2 whitespace-pre-wrap text-sm text-slate-300">“{r.note}”</p>}
              </div>
              <span data-testid={`app-request-status-${r.id}`}
                className={`rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${
                  r.status === "live" ? "border-emerald-500/40 text-emerald-400"
                  : r.status === "declined" ? "border-ink-600 text-slate-500"
                  : "border-gold/40 text-gold"}`}>
                {r.status_label}
              </span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button data-testid={`app-request-register-${r.id}`} size="sm" disabled={busy}
                onClick={() => onRegister?.(r)} className="bg-gold text-ink-900 hover:bg-gold-light">
                <Rocket className="mr-2 h-4 w-4" /> Register this app
              </Button>
              {STATUSES.map(([key, label]) => (
                <Button key={key} data-testid={`app-request-set-${key}-${r.id}`} variant="ghost"
                  size="sm" disabled={busy || r.status === key} onClick={() => setStatus(r, key)}
                  className="text-slate-400 hover:text-gold">{label}</Button>
              ))}
            </div>
            <Input data-testid={`app-request-note-${r.id}`} className="mt-2"
              placeholder="A note back to the operator (optional)"
              value={notes[r.id] ?? r.admin_note ?? ""}
              onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })} />
          </div>
        ))}
      </div>
    </div>
  );
};
